/**
 * Landing header + hero, per the FinminOS landing Figma:
 *
 *   ┌────────────────────────────────────────────────────────────┐
 *   │ logo · "Limited Seats…" + deadline · countdown · CTA       │  header band
 *   ├────────────────────────────────────────────────────────────┤
 *   │ badge                                    avatars/trust     │
 *   │ One System. / From Income / to Wealth.   ┌───────────────┐ │
 *   │ paragraph + checklist + CTA              │ dashboard mock│ │
 *   │                                          │  + hero girl  │ │
 *   └────────────────────────────────────────────────────────────┘
 *
 * The hero sits on a deepened forest surface (forest + black overlay) with a
 * large decorative ring, exactly like the design. On mobile everything stacks
 * and the dashboard/girl visual is hidden (per the mobile frame).
 */
import { CalendarDays, Check } from 'lucide-react';
import { BrandLogo } from '@finmin/ui';
import { Link } from '@tanstack/react-router';
import {
  HERO_CHECKLIST,
  ITR_DEADLINE_ISO,
  ITR_DEADLINE_LABEL,
  MOCKUP_ACTIONS,
} from './data';
import { pad2, useCountdown } from './use-countdown';
import { CtaLink } from './shared';

// ── Header band ───────────────────────────────────────────────────────────────

function CountdownBox({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="min-w-[2.9rem] rounded-md bg-amber px-2 py-1 text-center text-sm font-bold text-forest tabular-nums">
        {value}
      </span>
      <span className="mt-0.5 text-[10px] text-cream/70">{label}</span>
    </div>
  );
}

function DeadlineCountdown() {
  const c = useCountdown(ITR_DEADLINE_ISO);
  return (
    <div className="flex items-center gap-2" role="timer" aria-label="Time left until the ITR deadline">
      <CountdownBox value={pad2(c.days)} label="Days" />
      <CountdownBox value={pad2(c.hours)} label="Hours" />
      <CountdownBox value={pad2(c.mins)} label="Mins" />
      <CountdownBox value={pad2(c.secs)} label="Secs" />
    </div>
  );
}

export function LandingHeader() {
  return (
    <header className="sticky top-0 z-40 bg-forest shadow-md">
      {/* Desktop: single band. Mobile: logo+CTA row, then announcement strip. */}
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Link to="/" aria-label="FinminOS — home" className="shrink-0">
          <span className="sm:hidden"><BrandLogo wordColor="cream" height={26} /></span>
          <span className="hidden sm:block"><BrandLogo wordColor="cream" height={34} /></span>
        </Link>

        {/* Announcement + deadline (desktop) */}
        <div className="hidden lg:block">
          <p className="text-xs font-semibold text-amber">
            Limited Seats. Grab your seat before time expires
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-sm font-bold text-cream">
            <CalendarDays className="h-4 w-4" aria-hidden />
            {ITR_DEADLINE_LABEL}
          </p>
        </div>

        <div className="hidden md:block">
          <DeadlineCountdown />
        </div>

        <CtaLink className="shrink-0" />
      </div>

      {/* Mobile announcement strip */}
      <div className="border-t border-hero-border/40 px-4 py-3 text-center md:hidden">
        <p className="text-xs font-semibold text-amber">
          Limited Seats. Grab your seat before time expires
        </p>
        <p className="mt-1 flex items-center justify-center gap-1.5 text-sm font-bold text-cream">
          <CalendarDays className="h-4 w-4" aria-hidden />
          {ITR_DEADLINE_LABEL}
        </p>
        <div className="mt-2.5 flex justify-center">
          <DeadlineCountdown />
        </div>
      </div>
    </header>
  );
}

// ── Dashboard mockup (hero visual) ────────────────────────────────────────────

function DashboardMockup() {
  return (
    <div className="w-full max-w-xl overflow-hidden rounded-2xl bg-cream shadow-2xl">
      {/* Mock app header */}
      <div className="flex items-center justify-between bg-forest px-5 py-3.5">
        <BrandLogo wordColor="cream" height={22} />
        <span className="text-xs text-cream/70">Good morning</span>
      </div>

      <div className="space-y-4 p-5">
        {/* Stat tiles */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'NET WORTH', value: '₹48.3L', cls: 'text-forest' },
            { label: 'RETURNS', value: '+14.2%', cls: 'text-success' },
            { label: 'TAX SAVED', value: '₹1.4L', cls: 'text-amber' },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-border bg-white/70 px-3 py-3">
              <p className="text-[9px] font-semibold tracking-wider text-muted-foreground">{s.label}</p>
              <p className={`mt-0.5 text-lg font-bold tabular-nums ${s.cls}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-3 gap-3">
          {MOCKUP_ACTIONS.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center justify-center gap-2 rounded-xl bg-mint px-3 py-2.5 text-sm font-semibold text-forest">
              <Icon className="h-4 w-4" aria-hidden />
              {label}
            </div>
          ))}
        </div>

        {/* Expert recommendation note */}
        <div className="rounded-xl border-l-4 border-amber bg-butter px-4 py-3">
          <p className="text-[10px] font-bold tracking-wider text-amber">EXPERT RECOMMENDATION</p>
          <p className="mt-1 text-xs leading-relaxed text-forest">
            Your NPS employer contribution of ₹1.2L is unclaimed. Switching regimes saves you ₹34,000.
          </p>
        </div>

        {/* Mock buttons */}
        <div className="grid grid-cols-2 gap-3">
          <span className="rounded-xl bg-forest px-4 py-2.5 text-center text-sm font-semibold text-cream">
            File ITR →
          </span>
          <span className="rounded-xl border border-forest/30 px-4 py-2.5 text-center text-sm font-semibold text-forest">
            Tax Report
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────

export function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-forest-deep">
      {/* Decorative ring poking into the bottom-left corner. Its continuation
          below the boundary lives in TrustSection so the two halves read as
          one ring crossing the section edge, exactly like the Figma. */}
      <div
        className="pointer-events-none absolute -bottom-[319px] -left-[220px] hidden h-[470px] w-[470px] rounded-full border-[90px] border-black/25 lg:block"
        aria-hidden
      />
      {/* Soft lighter circle behind the girl, flush with the right edge (Figma) */}
      <div
        className="pointer-events-none absolute right-0 top-[125px] hidden h-[350px] w-[350px] rounded-full bg-cream/[0.05] lg:block"
        aria-hidden
      />

      {/* lg:min-h matches the Figma hero band (~900px below the header).
          pb: mobile needs bottom padding (the trusted-by cluster is the last
          element); desktop stays pb-0 so the girl sits flush with the edge. */}
      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-12 pt-12 sm:px-6 lg:min-h-[900px] lg:grid-cols-2 lg:gap-8 lg:pb-0 lg:pt-24">
        {/* Left column — copy */}
        <div className="text-center lg:pb-20 lg:text-left">
          <span className="inline-block rounded-full border border-amber/50 bg-amber/10 px-4 py-1.5 text-xs font-semibold text-amber">
            India&apos;s 1st Financial Operating System
          </span>

          <h1 className="mt-6 font-landing text-[2.6rem] font-bold leading-[1.05] text-cream sm:text-6xl lg:text-7xl">
            One System.
            <br />
            <em className="italic text-amber">From Income</em>
            <br />
            to Wealth.
          </h1>

          <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-hero-muted-foreground sm:text-base lg:mx-0">
            Managing your finances shouldn&apos;t happen once a year. FinminOS combines
            technology with expert guidance to help you file taxes, stay compliant, plan
            ahead, and make smarter money decisions throughout the year—all from one
            secure platform customized for your profile.
          </p>

          {/* 2×2 checklist. The mobile Figma pairs the items differently
              (short items first column-wise) — order-* handles the swap. */}
          <ul className="mx-auto mt-6 grid max-w-xl grid-cols-2 gap-x-4 gap-y-2.5 text-left sm:gap-x-8 lg:mx-0">
            {HERO_CHECKLIST.map((item, i) => (
              <li
                key={item}
                className={[
                  'flex items-start gap-2 text-sm font-medium text-cream',
                  ['order-1', 'order-4 sm:order-2', 'order-3', 'order-2 sm:order-4'][i] ?? '',
                ].join(' ')}
              >
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-amber" aria-hidden />
                {item}
              </li>
            ))}
          </ul>

          <div className="mt-8">
            <CtaLink size="lg" />
          </div>

          {/* Trusted-by cluster — mobile position per the mobile Figma:
              left-aligned below the CTA, three chips + "+", larger copy. */}
          <div className="mt-12 flex items-center gap-3 pl-5 text-left lg:hidden">
            <span className="flex -space-x-2" aria-hidden>
              {['RK', 'PS', 'AM'].map((initials) => (
                <span
                  key={initials}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-mint text-[11px] font-bold text-forest ring-2 ring-forest-deep"
                >
                  {initials}
                </span>
              ))}
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber text-[11px] font-bold text-white ring-2 ring-forest-deep">
                +
              </span>
            </span>
            <p className="text-sm text-hero-muted-foreground">
              Trusted by <span className="font-bold text-cream">500+ customers</span>
              <br />
              across India
            </p>
          </div>
        </div>

        {/* Right column — trusted-by avatars on top, then dashboard mockup with
            the hero girl standing IN FRONT of its right edge (per the Figma). */}
        <div className="hidden flex-col lg:flex">
          {/* Trusted-by avatars — top-right, aligned with the badge row */}
          <div className="flex items-center justify-end gap-3 pr-2">
            {/* Initials chips, as in the Figma (mint circles + amber "+") */}
            <span className="flex -space-x-2" aria-hidden>
              {['RK', 'PS', 'AM', 'VG'].map((initials) => (
                <span
                  key={initials}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-mint text-[10px] font-bold text-forest ring-2 ring-forest-deep"
                >
                  {initials}
                </span>
              ))}
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber text-[10px] font-bold text-white ring-2 ring-forest-deep">
                +
              </span>
            </span>
            <p className="text-xs text-hero-muted-foreground">
              Trusted by <span className="font-bold text-cream">500+ customers</span>
              <br />
              across India
            </p>
          </div>

          {/* NOTE: no z-index on the card wrapper — a stacking context would
              trap the z-30 floating badges below the z-20 girl. Card body
              stays z-auto (behind the girl); badges z-30 (above her).
              flex-1 stretches this wrapper to the section bottom so the
              bottom-anchored girl sits flush with the hero's edge (Figma). */}
          <div className="relative mt-10 flex-1">
            <div className="relative w-full max-w-[640px]">
              <DashboardMockup />

              {/* Floating: avg tax saved (floats just above the card's right third) */}
              <div className="absolute -top-9 right-28 z-30 rounded-2xl bg-amber px-4 py-3 text-white shadow-xl">
                <p className="text-xl font-extrabold leading-none">₹1.8L</p>
                <p className="mt-1 text-[10px] opacity-90">avg. tax saved / year</p>
              </div>

              {/* Floating: expert reviewed (overlaps the card's bottom-left corner) */}
              <div className="absolute -bottom-16 -left-10 z-30 rounded-2xl bg-white px-4 py-3 shadow-xl">
                <p className="text-[10px] font-bold tracking-widest text-muted-foreground">EXPERT REVIEWED</p>
                <p className="mt-0.5 text-lg font-bold text-forest">24 Hours</p>
                <p className="text-[11px] text-muted-foreground">ITR-V sent to inbox</p>
              </div>
            </div>

            {/* Hero girl — Photoroom cutout from the Figma, in FRONT of the card,
                overlapping its right third, extending past the container edge and
                sitting flush on the section's bottom edge (Figma: 534px tall,
                ~130px from the viewport's right edge at 1920). */}
            <img
              src="/hero-girl.png"
              alt="A FinminOS tax expert pointing at the dashboard"
              className="pointer-events-none absolute -right-32 bottom-0 z-20 h-[534px] w-auto object-contain drop-shadow-[0_12px_32px_rgba(0,0,0,0.45)] xl:-right-56"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
